import { setGrid } from '../Global/global_variables';
import { switcher } from '../Global/Switcher';
import { initGeodata, unloadGeodata } from '../Graphics/standardRenderer';
import { SerializationManager } from '../Loader/serialization/manager/SerializationManager';

import { GeometryUtils } from '@data/geometry/GeometryUtils';
import { Panels } from '@gui/constants/Panels';
import { getActiveLayerUID } from '@gui/store/persistent/persistentStore';
import type { ContextState, GridResources, UUID } from '@visualizer/types';
import { AllColorScheme, GeoObjectMode } from '@visualizer/types';
import { getViewer } from 'store/viewerStore';
import { ContextItems } from '../Geodata/ContextItems';
import Grid from '../Geodata/grid';
import Layer from '../Simulations/Layer';
import { Geodata } from './Geodata';
import { Boundary, OctTree } from './OctTree';
import { SimulationSet } from './SimulationSet';
import { requireResources } from 'store/assertions';
import { getLoadingStatus, LoadingStatus } from 'store/LoadingStore';

// Notifiations
import useNotificationStore from '@gui/store/notification';
import { NotificationType } from '@visualizer/types';

// Persistent store
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { FileFetcherV2 } from 'Loader/FileFetcherv2';
import { ApiRoutes } from 'store/ApiRoutes';

export class GridData {
    id: string;
    name: string;
    geodatas: Geodata[] = [];

    public contextItems: ContextItems | undefined;
    layersNumber = 0;

    octTree?: OctTree;

    constructor(gridResources: GridResources) {
        setGrid(this);
        this.applyCoordinateReferenceSystem(gridResources);
        this.name = gridResources.name;
        const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
        const localhostPrefix = import.meta.env.DEV || isLocalhost ? '[LOCALHOST] ' : '';
        document.title = `${localhostPrefix}Visualizer: ${gridResources.name}`;
        const geodatasJSON = gridResources.geodatas;
        this.id = gridResources.id;
        for (let geodataJSON of geodatasJSON) {
            const geodata = new Geodata(this, geodataJSON);
            this.geodatas.push(geodata);
        }
        this.geodatas.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }

    private applyCoordinateReferenceSystem(gridResources: GridResources) {
        const coordinateReferenceSystem =
            gridResources.params && 'coordinateReferenceSystem' in gridResources.params
                ? gridResources.params.coordinateReferenceSystem
                : null;
        const origin = coordinateReferenceSystem?.origin;
        const rotation = coordinateReferenceSystem?.rotation ?? 0;

        if (origin?.x == null || origin?.y == null || origin?.z == null) return;

        const isNationalCoordinateSystem = origin.x === 0 && origin.y === 0 && origin.z === 0 && rotation === 0;

        usePersistentStore.getState().setPanel(
            {
                coordinateSystem: {
                    ...(isNationalCoordinateSystem
                        ? {
                              mode: 'lv95' as const,
                          }
                        : {
                              mode: 'custom' as const,
                              centerX: origin.x,
                              centerY: origin.y,
                              centerZ: origin.z,
                              alpha: rotation !== 0 ? (rotation * 180) / Math.PI : 0,
                          }),
                },
            },
            Panels.SETTINGS
        );
    }

    initOctTree() {
        // Init Grid OctTree:
        const minX = Math.min(...Grid.boundingBox.map((vertex) => vertex.x));
        const minY = Math.min(...Grid.boundingBox.map((vertex) => vertex.y));
        const maxX = Math.max(...Grid.boundingBox.map((vertex) => vertex.x));
        const maxY = Math.max(...Grid.boundingBox.map((vertex) => vertex.y));

        const numSensors = Grid.sensorPointsN; // Grid.gridX * Grid.gridY * Grid.gridZ;
        const boundary = new Boundary(minX, minY, 0, maxX - minX, maxY - minY, (Grid.maxFloor + 1) * Grid.gridRes);
        this.octTree = new OctTree(boundary, numSensors, { rotationAngle: -Grid.gridAngle, rotationOrigin: Grid.gridOrigin });
        for (const item of Grid.sensorPoints) {
            if (item.getID() != -1) this.octTree.insert(item);
        }
    }

    async load(fileFetcher: FileFetcherV2) {
        await this.loadGrid(fileFetcher);

        await this.loadData(fileFetcher);
        this.selectInitialActiveLayer();
        this.initOctTree();
    }

    private async loadGrid(fileFetcher: FileFetcherV2) {
        const gridEndpoint = ApiRoutes.gridDownload(this.id);
        await fileFetcher.downloadFile('gridFile', gridEndpoint, { trackProgress: false });

        const serializationManager = SerializationManager.geomSerializationManager;
        await serializationManager.deserializeGeom('gridFile', Grid);

        // Find First Active SP index:
        let firstActiveIndex = 0;
        for (let sensorPoint of Grid.sensorPoints) {
            if (sensorPoint.getID() == -1) firstActiveIndex++;
            else {
                firstActiveIndex += Grid.gridZ - 1;
                break;
            }
        }
        Grid.firstActiveID = firstActiveIndex;

        // Compute:
        Grid.legalBorderArea = GeometryUtils.computeAreaFromBorder(Grid.legalBorder);

        //this.contextItems = new ContextItems();
        // this.contextItems.addToScene();
    }

    private async loadData(fileFetcher: FileFetcherV2) {
        // Discover simulation result queues for all geodatas before starting downloads,
        // so the loading UI has a stable total file count.
        const simulationPlansByGeodata = await Promise.all(this.geodatas.map((geodata) => geodata.discoverSimulationPlans(fileFetcher)));
        await Promise.all(this.geodatas.map((geodata, index) => geodata.loadData(fileFetcher, simulationPlansByGeodata[index])));

        const scene = getViewer().scene;
        this.contextItems = new ContextItems(scene!);
    }

    getLayerCollection() {
        return this.getSimulationSet().layerCollection;
    }

    // Select the initial active layer based on the following logic:
    // 1. In observation mode, keep the restored layer if it exists in the loaded grid.
    // 2. Outside observation mode, keep the restored layer only when it belongs to the same grid.
    // 3. Otherwise, try to find a "yearly sun" layer and fall back to the first available layer.
    private selectInitialActiveLayer() {
        const orderedLayers = this.getOrderedLayers();

        // If there are no layers at all, do nothing (active layer will be empty)
        if (orderedLayers.length === 0) return;

        // Try to find the preferred initial layer (yearly sun or first available)
        const preferredLayer = this.findPreferredInitialLayer(orderedLayers);
        if (!preferredLayer) return;

        const store = usePersistentStore.getState();
        const currentLayerId = store.layers.activeLayerID;
        // Check if the current active layer ID from Zustand exists in the currently loaded layers
        const currentExists = currentLayerId ? orderedLayers.some((layer) => layer.getId() === currentLayerId) : false;

        // Current grid
        const currentGridId = requireResources().gridId;
        // Stored active grid in Zustand
        const storedActiveGridId = store.global.activeGridId;
        const isSameGrid = !!currentGridId && !!storedActiveGridId && currentGridId === storedActiveGridId;
        const isGridMatchUnknown = !currentGridId || !storedActiveGridId;
        const hasUsableCurrentLayer = !!currentLayerId && currentExists;
        const isObservationMode = typeof window !== 'undefined' && new URL(window.location.href).searchParams.has('observation_id');

        const shouldKeepCurrentLayer = isObservationMode
            ? hasUsableCurrentLayer
            : hasUsableCurrentLayer && (isSameGrid || isGridMatchUnknown);

        if (!shouldKeepCurrentLayer) {
            store.setCurrentLayerId(preferredLayer.getId());
        }

        // Update active grid in Zustand if different from current
        if (currentGridId && storedActiveGridId !== currentGridId) {
            usePersistentStore.setState((state) => ({
                global: {
                    ...state.global,
                    activeGridId: currentGridId,
                },
            }));
        }
    }

    // Helper to find the preferred initial layer (yearly sun or first available)
    private findPreferredInitialLayer(orderedLayers: Layer[]): Layer | undefined {
        const yearlySunLayer = orderedLayers.find((layer) => this.isYearlySunLayer(layer));
        return yearlySunLayer ?? orderedLayers[0];
    }

    private isYearlySunLayer(layer: Layer): boolean {
        if (!layer.isSun()) return false;
        const name = layer.name?.toLowerCase() ?? '';
        return name.includes('yearly');
    }

    private getOrderedLayers(): Layer[] {
        const ordered: Layer[] = [];
        for (const geodata of this.geodatas) {
            for (const simulationSet of geodata.simulationSets) {
                ordered.push(...simulationSet.layerCollection);
            }
        }
        return ordered;
    }

    /** @deprecated */
    getAllLayers() {
        const tmpLayerCollection = [];
        for (let geodata of this.geodatas) {
            for (let simulationSet of geodata.simulationSets) {
                for (let layer of simulationSet.layerCollection) tmpLayerCollection.push(layer);
            }
        }
        return tmpLayerCollection;
    }

    /** @deprecated */
    getLayerCollectionByGroup(geodataID = 0, simulationSetID = 0) {
        return this.geodatas[geodataID].simulationSets[simulationSetID].layerCollectionByGroup;
    }

    /**
     * @deprecated
     * @returns
     */
    getGeodata(): Geodata {
        return this.getActiveGeodata();
    }

    getAllGeodatas() {
        return this.geodatas;
    }

    getActiveLayer(): Layer {
        const uid = getActiveLayerUID();
        const layer = this.searchLayerByUID(uid, { fallbackToFirst: true, warnOnFallback: false });

        if (layer.getId() !== uid) {
            const loadingStatus = getLoadingStatus();
            const isStartupFallback = !!uid && (loadingStatus === LoadingStatus.INITIALIZING || loadingStatus === LoadingStatus.LOADING);

            if (isStartupFallback) {
                return layer;
            }

            // Keep Zustand consistent
            usePersistentStore.getState().setCurrentLayerId(layer.getId());

            // Warn
            useNotificationStore
                .getState()
                .showNotification(
                    'Saved state refers to a layer that is not available in the currently loaded geodata. Loaded the first available layer instead.',
                    NotificationType.Warning
                );
        }

        return layer;
    }

    getSimulationSet(): SimulationSet {
        return this.getActiveLayer().parentSimulationSet;
    }
    getActiveGeodata() {
        return this.getSimulationSet().parentGeodata;
    }

    /** @deprecated */
    setActiveGeodata(_geodataID = 0) {
        // disposeGeodata();
        // this.disposeOldLayer();
        // worldStatus.data.ACTIVE_GEODATA_ID = geodataID;
        // initGeodata();
        // const currentGeodata = this.geodatas[worldStatus.data.ACTIVE_GEODATA_ID];
        // if (currentGeodata.simulationSets.length == 0) {
        //     // If there are no simulation sets, set mode to editGeodata
        //     setActiveMode(modes.editGeodata);
        // } else {
        //     if (worldStatus.data.ACTIVE_SIMULATION_SET_ID >= currentGeodata.simulationSets.length) worldStatus.data.ACTIVE_SIMULATION_SET_ID = 0;
        //     var currentSimulationSet = currentGeodata.simulationSets[worldStatus.data.ACTIVE_SIMULATION_SET_ID];
        //     if (currentSimulationSet.layerCollection.length == 0 || currentSimulationSet.hasEmptyLayer) {
        //         // If there are no layers in the current simulation set, set mode to editGeodata
        //         setActiveMode(modes.editGeodata);
        //     } else {
        //         // If none of the conditions for editGeodata are met, set mode to siteAnalysis
        //         setActiveMode(modes.siteAnalysis);
        //     }
        // }
        // // grid.geodatas[worldStatus.data.ACTIVE_GEODATA_ID].simulationSets.length == 0 ? setActiveMode(modes.editGeodata) : setActiveMode(modes.siteAnalysis);
        // // grid.geodatas[worldStatus.data.ACTIVE_GEODATA_ID].simulationSets[worldStatus.data.ACTIVE_SIMULATION_SET_ID].layerCollection.length == 0 ? setActiveMode(modes.editGeodata) : setActiveMode(modes.siteAnalysis);
        // this.loadNewLayer(worldStatus.data.ACTIVE_LAYER_ID);
    }
    unloadGeodata(id: UUID) {
        unloadGeodata(id);
    }

    loadActiveGeodata(colorScheme: AllColorScheme, mode: GeoObjectMode, context: ContextState) {
        initGeodata(colorScheme, mode, context);
        // Enable edit geodata mod if the geodata is empty
    }
    /**
     * @deprecated
     * @returns
     */
    disposeOldLayer() {
        const oldLayer = this.getActiveLayer(); // Questo deve essere applicato con i valori precedenti
        if (!oldLayer) return; // nothing to dispose here
        if (!oldLayer.getActiveFrame()) return;

        // if (oldLayer instanceof LayerStandard) {
        //     oldLayer.unloadRays();
        //     oldLayer.unloadIntersections();
        // }
    }
    /**
     * @deprecated Use `loadNewLayerByUID` instead.
     * @param id
     * @returns
     */
    loadNewLayer(_id: number) {
        // if (worldStatus.data.IS_MIXER_ACTIVE) return;
        // worldStatus.data.ACTIVE_LAYER_ID = id;
        // if (this.getLayerCollection().length <= 0) return;
        // const newLayer = this.getActiveLayer();
        // if (newLayer instanceof LayerStandard) {
        //     newLayer.unloadRays();
        //     newLayer.unloadIntersections();
        // }
        // // Update layer visualization:
        // const frame = newLayer.getCurrentFrame();
        // frame.computeMinMaxValues();
        // frame.mapSensorColors(newLayer.status.segments.IS_LOCAL); // Keeps the map when switching between layers
        // frame.updateShadows();
        // newLayer.status.operator.UPDATE_HEATMAP = true;
        // switcher.REFRESH_GRADIENT = false;
        // switcher.REFRESH_GRADIENT_VALUES = false;
        // if (activeMode.panels.segments) activeMode.panels.segments.settings.visible = this.getActiveLayer().getFrameCollection().length != 1;
    }

    loadNewLayer2_0(newLayer: Layer) {
        // Update layer visualization:
        const frame = newLayer.getActiveFrame();

        frame.updateShadows();

        // TODO: Replace with new GUI
        // newLayer.status.operator.UPDATE_HEATMAP = true;
        switcher.REFRESH_GRADIENT = false;
        switcher.REFRESH_GRADIENT_VALUES = false;
    }

    /**
     * @deprecated Use `setActiveLayerByUID` instead.
     */
    setActiveLayer(id: number) {
        this.disposeOldLayer();
        this.loadNewLayer(id);
    }

    setActiveLayerByUID(UID: string) {
        const newLayer = this.searchLayerByUID(UID, { fallbackToFirst: true, warnOnFallback: true });

        // If we fell back, keep Zustand consistent
        if (newLayer.getId() !== UID) {
            usePersistentStore.getState().setCurrentLayerId(newLayer.getId());
        }

        this.disposeOldLayer();
        this.loadNewLayer2_0(newLayer);
    }

    getGeodataIdOf(UID: string): string {
        for (let geodata of this.geodatas) {
            for (let simulationSet of geodata.simulationSets) {
                if (UID.startsWith(simulationSet.id)) {
                    return simulationSet.parentGeodata.id;
                }
            }
        }
        throw new Error(`Geodata of UID: <${UID}> not found.`);
    }
    public searchLayerByUID(UID: string, opts?: { fallbackToFirst?: boolean; warnOnFallback?: boolean }): Layer {
        for (let geodata of this.geodatas) {
            for (let simulationSet of geodata.simulationSets) {
                for (let layer of simulationSet.layerCollection) {
                    if (layer.getId() === UID) {
                        return layer;
                    }
                }
            }
        }

        if (opts?.fallbackToFirst) {
            const fallback = this.getFirstAvailableLayer();
            if (!fallback) {
                throw new Error(`Layer with UID: <${UID}> not found (and no layers are available). ${this.describeLayerAvailability()}`);
            } else if (opts?.warnOnFallback) {
                useNotificationStore.getState().showNotification('First available layer used', NotificationType.Warning);
            }
            return fallback;
        }

        throw new Error(`Layer with UID: <${UID}> not found.`);
    }

    // Helper for the fallback in the searchLayerByUID
    private getFirstAvailableLayer(): Layer | undefined {
        const fallbackErrors: string[] = [];

        for (const geodata of this.geodatas) {
            for (const simulationSet of geodata.simulationSets) {
                for (const layer of simulationSet.layerCollection) {
                    return layer;
                }
            }
        }

        for (const geodata of this.geodatas) {
            try {
                const fallbackLayer = geodata.ensureAtLeastOneLayer();
                if (fallbackLayer) {
                    return fallbackLayer;
                }
            } catch (error) {
                fallbackErrors.push(
                    `${geodata.id}: ${error instanceof Error ? error.message : 'Unknown error while creating empty layer'}`
                );
            }
        }

        if (fallbackErrors.length > 0) {
            console.error('[GridData] Failed to create fallback empty layer', {
                errors: fallbackErrors,
                availability: this.describeLayerAvailability(),
            });
        }
        return undefined;
    }

    private describeLayerAvailability(): string {
        const geodataCount = this.geodatas.length;
        const simulationSetCount = this.geodatas.reduce((count, geodata) => count + geodata.simulationSets.length, 0);
        const layerCount = this.geodatas.reduce(
            (count, geodata) =>
                count + geodata.simulationSets.reduce((simCount, simulationSet) => simCount + simulationSet.layerCollection.length, 0),
            0
        );
        return `geodatas=${geodataCount}, simulationSets=${simulationSetCount}, layers=${layerCount}`;
    }

    findLayersByKind(simKind: number): Layer[] {
        const matchingLayers: Layer[] = [];

        for (let geodata of this.geodatas) {
            for (let simulationSet of geodata.simulationSets) {
                for (let layer of simulationSet.layerCollection) {
                    if (layer.isOfType(simKind)) {
                        matchingLayers.push(layer);
                    }
                }
            }
        }

        return matchingLayers;
    }
}
